import { useCart } from '../../contexts/CartContext';
import { Link } from 'react-router-dom';

interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CartSidebar({ isOpen, onClose }: CartSidebarProps) {
  const { items, removeFromCart, updateQuantity, getCartTotal } = useCart();

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-50 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[480px] bg-white z-50 shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">
            <h2 className="font-serif text-2xl text-reset-dark">Your Bag</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-reset-dark hover:text-reset-sage transition-colors cursor-pointer"
              aria-label="Close cart"
            >
              <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <i className="ri-shopping-bag-3-line text-6xl text-gray-300 mb-4 w-16 h-16 flex items-center justify-center"></i>
                <p className="text-gray-500 mb-6">Your bag is empty</p>
                <Link
                  to="/shop"
                  onClick={onClose}
                  className="text-reset-sage hover:text-reset-sage-hover text-sm font-semibold tracking-wide transition-colors cursor-pointer"
                >
                  Continue shopping
                </Link>
              </div>
            ) : (
              <div className="space-y-6">
                {items.map((item) => (
                  <div key={item.variantId} className="flex gap-4">
                    {/* Product Image */}
                    <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-medium text-reset-dark text-sm">
                            {item.name}
                          </h3>
                          <p className="text-gray-500 text-xs mt-1">
                            Size: {item.size}
                          </p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.variantId)}
                          className="text-gray-400 hover:text-reset-dark transition-colors cursor-pointer"
                          aria-label="Remove item"
                        >
                          <i className="ri-delete-bin-line text-lg w-5 h-5 flex items-center justify-center"></i>
                        </button>
                      </div>

                      <div className="flex justify-between items-center mt-3">
                        {/* Quantity Selector */}
                        <div className="flex items-center gap-2 border border-gray-300 rounded-md">
                          <button
                            onClick={() =>
                              updateQuantity(
                                item.variantId,
                                Math.max(1, item.quantity - 1)
                              )
                            }
                            className="w-8 h-8 flex items-center justify-center text-reset-dark hover:bg-gray-100 transition-colors cursor-pointer"
                            aria-label="Decrease quantity"
                          >
                            <i className="ri-subtract-line text-sm w-4 h-4 flex items-center justify-center"></i>
                          </button>
                          <span className="text-sm font-medium w-8 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateQuantity(
                                item.variantId,
                                Math.min(item.maxStock, item.quantity + 1)
                              )
                            }
                            disabled={item.quantity >= item.maxStock}
                            className="w-8 h-8 flex items-center justify-center text-reset-dark hover:bg-gray-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                            aria-label="Increase quantity"
                          >
                            <i className="ri-add-line text-sm w-4 h-4 flex items-center justify-center"></i>
                          </button>
                        </div>

                        {/* Price */}
                        <p className="font-medium text-reset-dark">
                          £{(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-gray-200 px-8 py-6">
              <div className="flex justify-between items-center mb-6">
                <span className="text-sm font-medium text-reset-dark">
                  Subtotal
                </span>
                <span className="text-xl font-serif text-reset-dark">
                  £{getCartTotal().toFixed(2)}
                </span>
              </div>
              <Link
                to="/checkout"
                onClick={onClose}
                className="block w-full bg-reset-sage text-white hover:bg-reset-sage-hover px-6 py-4 rounded-full text-sm font-semibold tracking-widest transition-all duration-300 text-center cursor-pointer whitespace-nowrap"
              >
                PROCEED TO CHECKOUT
              </Link>
              <button
                onClick={onClose}
                className="block w-full mt-3 text-reset-sage hover:text-reset-sage-hover text-sm font-medium transition-colors cursor-pointer"
              >
                Continue shopping
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
